import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Car, ArrowLeft, Wrench, Fuel, MapPin, AlertTriangle, Plus, Trash2, 
  Gauge, DollarSign, Calendar, Camera, Settings, Route
} from 'lucide-react';
import { carApi, tokenApi } from '@/lib/api';
import { toast } from 'sonner';
import { formatDate, formatCurrency, fileToBase64, isAuthenticated } from '@/lib/utils';
import SponsorSection from '@/components/SponsorSection';
import { ModuleChat } from '@/components/ModuleChat';

const SERVICE_TYPES = [
  'Oil Change', 'Brake Pads', 'Tire Rotation', 'Battery Replacement',
  'Air Filter', 'Transmission Service', 'Coolant Flush', 'Spark Plugs',
  'Alignment', 'Inspection', 'Other'
];

const DAMAGE_LEVELS = [
  { value: 'minor', label: 'Minor', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'moderate', label: 'Moderate', color: 'bg-orange-100 text-orange-700' },
  { value: 'major', label: 'Major', color: 'bg-red-100 text-red-700' },
];

export default function CarDiaryModule() {
  const { tokenId } = useParams();
  const navigate = useNavigate();
  const receiptRef = useRef(null);
  const photoRef = useRef(null);

  const [dashboard, setDashboard] = useState(null);
  const [services, setServices] = useState([]);
  const [fuelLogs, setFuelLogs] = useState([]);
  const [fuelStats, setFuelStats] = useState(null);
  const [trips, setTrips] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [sponsorInfo, setSponsorInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Dialogs
  const [showVehicleSetup, setShowVehicleSetup] = useState(false);
  const [showAddService, setShowAddService] = useState(false);
  const [showAddFuel, setShowAddFuel] = useState(false);
  const [showAddTrip, setShowAddTrip] = useState(false);
  const [showAddIncident, setShowAddIncident] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Vehicle form
  const [vMake, setVMake] = useState('');
  const [vModel, setVModel] = useState('');
  const [vYear, setVYear] = useState(new Date().getFullYear().toString());
  const [vColor, setVColor] = useState('');
  const [vPlate, setVPlate] = useState('');
  const [vVin, setVVin] = useState('');
  const [vPrice, setVPrice] = useState('');
  const [vPurchaseDate, setVPurchaseDate] = useState('');
  const [vOdometer, setVOdometer] = useState('0');

  // Service form
  const [sType, setSType] = useState('Oil Change');
  const [sOdometer, setSOdometer] = useState('');
  const [sCost, setSCost] = useState('');
  const [sProvider, setSProvider] = useState('');
  const [sNotes, setSNotes] = useState('');
  const [sDate, setSDate] = useState('');
  const [sReceipt, setSReceipt] = useState(null);

  // Fuel form
  const [fOdometer, setFOdometer] = useState('');
  const [fLiters, setFLiters] = useState('');
  const [fCost, setFCost] = useState('');
  const [fStation, setFStation] = useState('');
  const [fDate, setFDate] = useState('');

  // Trip form
  const [tStart, setTStart] = useState('');
  const [tEnd, setTEnd] = useState('');
  const [tFuelCost, setTFuelCost] = useState('');
  const [tNotes, setTNotes] = useState('');
  const [tDate, setTDate] = useState('');

  // Incident form
  const [iDesc, setIDesc] = useState('');
  const [iDamage, setIDamage] = useState('minor');
  const [iClaim, setIClaim] = useState(false);
  const [iDate, setIDate] = useState('');
  const [iPhotos, setIPhotos] = useState([]);

  useEffect(() => {
    if (!isAuthenticated()) { navigate(`/c/${tokenId}`); return; }
    loadData();
    loadSponsorInfo();
  }, [tokenId]);

  const loadData = async () => {
    try {
      const [dashRes, servRes, fuelRes, statsRes, tripRes, incRes] = await Promise.all([
        carApi.getDashboard(tokenId),
        carApi.getServices(tokenId),
        carApi.getFuelLogs(tokenId),
        carApi.getFuelStats(tokenId),
        carApi.getTrips(tokenId),
        carApi.getIncidents(tokenId),
      ]);
      setDashboard(dashRes.data);
      setServices(servRes.data);
      setFuelLogs(fuelRes.data);
      setFuelStats(statsRes.data);
      setTrips(tripRes.data);
      setIncidents(incRes.data);
      if (!dashRes.data.vehicle) setShowVehicleSetup(true);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSponsorInfo = async () => {
    try { const r = await tokenApi.getSponsorInfo(tokenId, 'car'); setSponsorInfo(r.data); } catch {}
  };

  const handleSaveVehicle = async () => {
    if (!vMake || !vModel || !vYear) return;
    setIsSubmitting(true);
    try {
      await carApi.createVehicle({
        token_id: tokenId, make: vMake, model: vModel, year: parseInt(vYear),
        color: vColor || undefined, plate_number: vPlate || undefined, vin: vVin || undefined,
        purchase_price: vPrice ? parseFloat(vPrice) : undefined, purchase_date: vPurchaseDate || undefined,
        current_odometer: parseInt(vOdometer) || 0,
      });
      toast.success('Vehicle saved!');
      setShowVehicleSetup(false);
      loadData();
    } catch (error) { toast.error('Failed to save vehicle'); }
    finally { setIsSubmitting(false); }
  };

  const handleAddService = async () => {
    if (!sType) return;
    setIsSubmitting(true);
    try {
      await carApi.createService({
        token_id: tokenId, service_type: sType, odometer: parseInt(sOdometer) || 0,
        cost: parseFloat(sCost) || 0, service_provider: sProvider || undefined,
        notes: sNotes || undefined, receipt_base64: sReceipt || undefined,
        service_date: sDate || undefined,
      });
      toast.success('Service logged!');
      setShowAddService(false);
      setSType('Oil Change'); setSOdometer(''); setSCost(''); setSProvider(''); setSNotes(''); setSDate(''); setSReceipt(null);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    finally { setIsSubmitting(false); }
  };

  const handleAddFuel = async () => {
    if (!fOdometer || !fLiters || !fCost) return;
    setIsSubmitting(true);
    try {
      await carApi.createFuelLog({
        token_id: tokenId, odometer: parseInt(fOdometer), liters: parseFloat(fLiters),
        cost: parseFloat(fCost), station: fStation || undefined, fuel_date: fDate || undefined,
      });
      toast.success('Fuel log added!');
      setShowAddFuel(false);
      setFOdometer(''); setFLiters(''); setFCost(''); setFStation(''); setFDate('');
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    finally { setIsSubmitting(false); }
  };

  const handleAddTrip = async () => {
    if (!tStart || !tEnd) return;
    setIsSubmitting(true);
    try {
      await carApi.createTrip({
        token_id: tokenId, start_odometer: parseInt(tStart), end_odometer: parseInt(tEnd),
        fuel_cost: tFuelCost ? parseFloat(tFuelCost) : undefined, notes: tNotes || undefined,
        trip_date: tDate || undefined,
      });
      toast.success('Trip logged!');
      setShowAddTrip(false);
      setTStart(''); setTEnd(''); setTFuelCost(''); setTNotes(''); setTDate('');
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    finally { setIsSubmitting(false); }
  };

  const handleAddIncident = async () => {
    if (!iDesc) return;
    setIsSubmitting(true);
    try {
      await carApi.createIncident({
        token_id: tokenId, description: iDesc, damage_level: iDamage,
        insurance_claim: iClaim, photos_base64: iPhotos, incident_date: iDate || undefined,
      });
      toast.success('Incident logged!');
      setShowAddIncident(false);
      setIDesc(''); setIDamage('minor'); setIClaim(false); setIDate(''); setIPhotos([]);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    finally { setIsSubmitting(false); }
  };

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const b64 = await fileToBase64(file); setSReceipt(b64); toast.success('Receipt attached'); } catch { toast.error('Failed to process file'); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const b64 = await fileToBase64(file); setIPhotos(prev => [...prev, b64]); toast.success('Photo added'); } catch { toast.error('Failed'); }
  };

  const vehicle = dashboard?.vehicle;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="car-diary-module">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-slate-400 hover:text-slate-600" data-testid="back-to-dashboard">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
              <Car className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h1 className="font-bold text-lg text-slate-900">Memora Car Diary</h1>
              <p className="text-xs text-slate-500">{vehicle ? `${vehicle.make} ${vehicle.model} ${vehicle.year}` : 'Set up your vehicle'}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => { if (vehicle) { setVMake(vehicle.make); setVModel(vehicle.model); setVYear(String(vehicle.year)); setVColor(vehicle.color || ''); setVPlate(vehicle.plate_number || ''); setVVin(vehicle.vin || ''); setVPrice(vehicle.purchase_price ? String(vehicle.purchase_price) : ''); setVPurchaseDate(vehicle.purchase_date || ''); setVOdometer(String(vehicle.current_odometer || 0)); } setShowVehicleSetup(true); }} data-testid="vehicle-settings-btn">
            <Settings className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Summary Cards */}
        {vehicle && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            {[
              { label: 'Odometer', value: `${(vehicle.current_odometer || 0).toLocaleString()} km`, icon: Gauge, color: 'text-slate-700' },
              { label: 'Maintenance', value: formatCurrency(dashboard?.total_maintenance_cost || 0, 'PHP'), icon: Wrench, color: 'text-blue-600' },
              { label: 'Fuel', value: formatCurrency(dashboard?.total_fuel_cost || 0, 'PHP'), icon: Fuel, color: 'text-amber-600' },
              { label: 'Trips', value: `${dashboard?.total_trips || 0}`, icon: Route, color: 'text-teal-600' },
              { label: 'Last Service', value: dashboard?.last_service ? formatDate(dashboard.last_service.service_date) : 'None', icon: Calendar, color: 'text-slate-500' },
            ].map((card, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="bg-white rounded-xl border border-slate-100 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <card.icon className={`w-4 h-4 ${card.color}`} />
                  <span className="text-xs text-slate-500">{card.label}</span>
                </div>
                <p className={`text-lg font-bold ${card.color}`}>{card.value}</p>
              </motion.div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-100 rounded-xl mb-6 p-1 w-full grid grid-cols-4">
            <TabsTrigger value="overview" className="rounded-lg text-xs sm:text-sm" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="fuel" className="rounded-lg text-xs sm:text-sm" data-testid="tab-fuel">Fuel</TabsTrigger>
            <TabsTrigger value="trips" className="rounded-lg text-xs sm:text-sm" data-testid="tab-trips">Trips</TabsTrigger>
            <TabsTrigger value="incidents" className="rounded-lg text-xs sm:text-sm" data-testid="tab-incidents">Incidents</TabsTrigger>
          </TabsList>

          {/* Overview Tab - Maintenance Services */}
          <TabsContent value="overview">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">Maintenance Log</h2>
              <Button onClick={() => setShowAddService(true)} className="bg-orange-500 hover:bg-orange-600 text-white rounded-full" size="sm" data-testid="add-service-btn">
                <Plus className="w-4 h-4 mr-1" /> Log Service
              </Button>
            </div>
            {services.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center"><Wrench className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">No maintenance records yet</p></div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100">
                {services.map((s, i) => (
                  <div key={s.id} className="p-4 flex items-center justify-between" data-testid={`service-${s.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center"><Wrench className="w-5 h-5 text-blue-600" /></div>
                      <div>
                        <p className="font-medium text-slate-900">{s.service_type}</p>
                        <p className="text-xs text-slate-500">{s.service_provider && `${s.service_provider} · `}{formatDate(s.service_date)} · {s.odometer?.toLocaleString()} km</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{formatCurrency(s.cost, 'PHP')}</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={async () => { await carApi.deleteService(s.id); toast.success('Deleted'); loadData(); }} data-testid={`delete-service-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Fuel Tab */}
          <TabsContent value="fuel">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">Fuel Tracker</h2>
              <Button onClick={() => setShowAddFuel(true)} className="bg-amber-500 hover:bg-amber-600 text-white rounded-full" size="sm" data-testid="add-fuel-btn">
                <Plus className="w-4 h-4 mr-1" /> Log Fuel
              </Button>
            </div>
            {fuelStats && fuelStats.logs_count > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {[
                  { label: 'Total Cost', value: formatCurrency(fuelStats.total_cost, 'PHP') },
                  { label: 'Total Liters', value: `${fuelStats.total_liters} L` },
                  { label: 'Avg Cost/L', value: formatCurrency(fuelStats.avg_cost_per_liter, 'PHP') },
                  { label: 'Avg L/100km', value: `${fuelStats.avg_consumption_per_100km}` },
                ].map((stat, i) => (
                  <div key={i} className="bg-white rounded-xl border border-slate-100 p-3 text-center">
                    <p className="text-xs text-slate-500">{stat.label}</p>
                    <p className="text-base font-bold text-slate-900">{stat.value}</p>
                  </div>
                ))}
              </div>
            )}
            {fuelLogs.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center"><Fuel className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">No fuel logs yet</p></div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100">
                {fuelLogs.map((f) => (
                  <div key={f.id} className="p-4 flex items-center justify-between" data-testid={`fuel-${f.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center"><Fuel className="w-5 h-5 text-amber-600" /></div>
                      <div>
                        <p className="font-medium text-slate-900">{f.liters} L{f.station ? ` at ${f.station}` : ''}</p>
                        <p className="text-xs text-slate-500">{formatDate(f.fuel_date)} · {f.odometer?.toLocaleString()} km</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-amber-600">{formatCurrency(f.cost, 'PHP')}</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={async () => { await carApi.deleteFuelLog(f.id); toast.success('Deleted'); loadData(); }}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Trips Tab */}
          <TabsContent value="trips">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">Trip Log</h2>
              <Button onClick={() => setShowAddTrip(true)} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full" size="sm" data-testid="add-trip-btn">
                <Plus className="w-4 h-4 mr-1" /> Log Trip
              </Button>
            </div>
            {trips.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center"><Route className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">No trips logged yet</p></div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100">
                {trips.map((t) => (
                  <div key={t.id} className="p-4 flex items-center justify-between" data-testid={`trip-${t.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center"><Route className="w-5 h-5 text-teal-600" /></div>
                      <div>
                        <p className="font-medium text-slate-900">{t.distance} km</p>
                        <p className="text-xs text-slate-500">{formatDate(t.trip_date)}{t.notes ? ` · ${t.notes}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {t.fuel_cost > 0 && <span className="text-sm text-slate-600">{formatCurrency(t.fuel_cost, 'PHP')}</span>}
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={async () => { await carApi.deleteTrip(t.id); toast.success('Deleted'); loadData(); }}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Incidents Tab */}
          <TabsContent value="incidents">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">Incidents</h2>
              <Button onClick={() => setShowAddIncident(true)} className="bg-red-500 hover:bg-red-600 text-white rounded-full" size="sm" data-testid="add-incident-btn">
                <Plus className="w-4 h-4 mr-1" /> Log Incident
              </Button>
            </div>
            {incidents.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center"><AlertTriangle className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">No incidents recorded</p></div>
            ) : (
              <div className="space-y-3">
                {incidents.map((inc) => {
                  const dmg = DAMAGE_LEVELS.find(d => d.value === inc.damage_level) || DAMAGE_LEVELS[0];
                  return (
                    <div key={inc.id} className="bg-white rounded-xl border border-slate-100 p-4" data-testid={`incident-${inc.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center mt-0.5"><AlertTriangle className="w-5 h-5 text-red-500" /></div>
                          <div>
                            <p className="font-medium text-slate-900">{inc.description}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${dmg.color}`}>{dmg.label}</span>
                              {inc.insurance_claim && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">Insurance Claim</span>}
                              <span className="text-xs text-slate-400">{formatDate(inc.incident_date)}</span>
                            </div>
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={async () => { await carApi.deleteIncident(inc.id); toast.success('Deleted'); loadData(); }}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Sponsor Section */}
        <SponsorSection sponsorInfo={sponsorInfo} tokenId={tokenId} moduleType="car" className="mt-8" />
      </main>

      <ModuleChat sponsorInfo={sponsorInfo} />

      {/* Vehicle Setup Dialog */}
      <Dialog open={showVehicleSetup} onOpenChange={setShowVehicleSetup}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>Vehicle Profile</DialogTitle><DialogDescription>Set up your vehicle details</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Make *</label><Input value={vMake} onChange={e => setVMake(e.target.value)} placeholder="Toyota" className="rounded-xl" data-testid="vehicle-make" /></div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Model *</label><Input value={vModel} onChange={e => setVModel(e.target.value)} placeholder="Fortuner" className="rounded-xl" data-testid="vehicle-model" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Year *</label><Input type="number" value={vYear} onChange={e => setVYear(e.target.value)} className="rounded-xl" data-testid="vehicle-year" /></div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Color</label><Input value={vColor} onChange={e => setVColor(e.target.value)} placeholder="White" className="rounded-xl" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Plate Number</label><Input value={vPlate} onChange={e => setVPlate(e.target.value)} placeholder="ABC 1234" className="rounded-xl" data-testid="vehicle-plate" /></div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">VIN</label><Input value={vVin} onChange={e => setVVin(e.target.value)} className="rounded-xl" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Purchase Price</label><Input type="number" value={vPrice} onChange={e => setVPrice(e.target.value)} className="rounded-xl" /></div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Purchase Date</label><Input type="date" value={vPurchaseDate} onChange={e => setVPurchaseDate(e.target.value)} className="rounded-xl" /></div>
            </div>
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Current Odometer (km)</label><Input type="number" value={vOdometer} onChange={e => setVOdometer(e.target.value)} className="rounded-xl" data-testid="vehicle-odometer" /></div>
            <Button onClick={handleSaveVehicle} disabled={isSubmitting || !vMake || !vModel} className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-full" data-testid="save-vehicle-btn">{isSubmitting ? 'Saving...' : 'Save Vehicle'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Service Dialog */}
      <Dialog open={showAddService} onOpenChange={setShowAddService}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Log Service</DialogTitle><DialogDescription>Record a maintenance service</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Service Type</label>
              <Select value={sType} onValueChange={setSType}><SelectTrigger className="rounded-xl" data-testid="service-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SERVICE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Odometer</label><Input type="number" value={sOdometer} onChange={e => setSOdometer(e.target.value)} placeholder="km" className="rounded-xl" data-testid="service-odometer" /></div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Cost</label><Input type="number" value={sCost} onChange={e => setSCost(e.target.value)} placeholder="0.00" className="rounded-xl" data-testid="service-cost" /></div>
            </div>
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Service Provider</label><Input value={sProvider} onChange={e => setSProvider(e.target.value)} placeholder="Toyota Service Center" className="rounded-xl" /></div>
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Date</label><Input type="date" value={sDate} onChange={e => setSDate(e.target.value)} className="rounded-xl" /></div>
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Notes</label><Input value={sNotes} onChange={e => setSNotes(e.target.value)} placeholder="Additional notes..." className="rounded-xl" /></div>
            <div>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => receiptRef.current?.click()}><Camera className="w-4 h-4 mr-1" />{sReceipt ? 'Receipt Attached' : 'Attach Receipt'}</Button>
              <input ref={receiptRef} type="file" accept="image/*" className="hidden" onChange={handleReceiptUpload} />
            </div>
            <Button onClick={handleAddService} disabled={isSubmitting || !sType} className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-full" data-testid="confirm-service-btn">{isSubmitting ? 'Saving...' : 'Log Service'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Fuel Dialog */}
      <Dialog open={showAddFuel} onOpenChange={setShowAddFuel}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Log Fuel</DialogTitle><DialogDescription>Record a fuel fill-up</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Odometer *</label><Input type="number" value={fOdometer} onChange={e => setFOdometer(e.target.value)} placeholder="km" className="rounded-xl" data-testid="fuel-odometer" /></div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Liters *</label><Input type="number" step="0.01" value={fLiters} onChange={e => setFLiters(e.target.value)} className="rounded-xl" data-testid="fuel-liters" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Cost *</label><Input type="number" step="0.01" value={fCost} onChange={e => setFCost(e.target.value)} className="rounded-xl" data-testid="fuel-cost" /></div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Station</label><Input value={fStation} onChange={e => setFStation(e.target.value)} placeholder="Petron" className="rounded-xl" /></div>
            </div>
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Date</label><Input type="date" value={fDate} onChange={e => setFDate(e.target.value)} className="rounded-xl" /></div>
            <Button onClick={handleAddFuel} disabled={isSubmitting || !fOdometer || !fLiters || !fCost} className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-full" data-testid="confirm-fuel-btn">{isSubmitting ? 'Saving...' : 'Log Fuel'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Trip Dialog */}
      <Dialog open={showAddTrip} onOpenChange={setShowAddTrip}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Log Trip</DialogTitle><DialogDescription>Record a journey</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Start Odo *</label><Input type="number" value={tStart} onChange={e => setTStart(e.target.value)} placeholder="km" className="rounded-xl" data-testid="trip-start" /></div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">End Odo *</label><Input type="number" value={tEnd} onChange={e => setTEnd(e.target.value)} placeholder="km" className="rounded-xl" data-testid="trip-end" /></div>
            </div>
            {tStart && tEnd && parseInt(tEnd) > parseInt(tStart) && (
              <p className="text-sm text-teal-600 font-medium">Distance: {parseInt(tEnd) - parseInt(tStart)} km</p>
            )}
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Fuel Cost</label><Input type="number" step="0.01" value={tFuelCost} onChange={e => setTFuelCost(e.target.value)} className="rounded-xl" /></div>
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Date</label><Input type="date" value={tDate} onChange={e => setTDate(e.target.value)} className="rounded-xl" /></div>
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Notes</label><Input value={tNotes} onChange={e => setTNotes(e.target.value)} placeholder="Manila to Tagaytay" className="rounded-xl" /></div>
            <Button onClick={handleAddTrip} disabled={isSubmitting || !tStart || !tEnd} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="confirm-trip-btn">{isSubmitting ? 'Saving...' : 'Log Trip'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Incident Dialog */}
      <Dialog open={showAddIncident} onOpenChange={setShowAddIncident}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Log Incident</DialogTitle><DialogDescription>Record an accident or incident</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Description *</label><Input value={iDesc} onChange={e => setIDesc(e.target.value)} placeholder="Minor fender bender at parking lot" className="rounded-xl" data-testid="incident-desc" /></div>
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Damage Level</label>
              <Select value={iDamage} onValueChange={setIDamage}><SelectTrigger className="rounded-xl" data-testid="incident-damage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DAMAGE_LEVELS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Date</label><Input type="date" value={iDate} onChange={e => setIDate(e.target.value)} className="rounded-xl" /></div>
            <div className="flex items-center gap-3">
              <input type="checkbox" checked={iClaim} onChange={e => setIClaim(e.target.checked)} id="ins-claim" className="rounded" />
              <label htmlFor="ins-claim" className="text-sm text-slate-700">Insurance Claim Filed</label>
            </div>
            <div>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => photoRef.current?.click()}><Camera className="w-4 h-4 mr-1" />Add Photo ({iPhotos.length})</Button>
              <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
            </div>
            <Button onClick={handleAddIncident} disabled={isSubmitting || !iDesc} className="w-full bg-red-500 hover:bg-red-600 text-white rounded-full" data-testid="confirm-incident-btn">{isSubmitting ? 'Saving...' : 'Log Incident'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
